import {
  buildPreregistrationPayload,
  buildSubmitFormData,
  getContactPhones,
} from "../utils/payload-mapper";
import useCatalogs from "./use-catalogs";
import useDocuments from "./use-documents";
import { getDocumentsTotalSize } from "../utils/document-policy";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToasts } from "react-toast-notifications";
import {
  INITIAL_STATE,
  TAXPAYER_NOT_FOUND_MESSAGE,
  DUPLICATED_PROVIDER_RNC_MESSAGE,
  RECOVERED_SUBMISSION_MESSAGE,
  RECOVERY_ATTEMPT_DELAYS_MS,
  INITIAL_IDENTITY_VALIDATION,
  INITIAL_REPRESENTATIVE_VALIDATION,
  RESET_IDENTITY_VALIDATION_FIELDS,
  RESET_AFTER_IDENTITY_CHANGE,
} from "../config/constants";
import {
  normalizeText,
  normalizePassport,
  buildUsernamePreview,
  normalizeDocumentByType,
  formatCedula,
  formatRnc,
  getDocumentValidationMessage,
} from "../utils/identity-utils";
import {
  getRequiredSupportingDocumentRequirements,
  areDocumentRequirementsCompleted,
  getRequiredSupportingDocumentsCount,
} from "../config/document-requirements";
import {
  wait,
  isUploadNetworkError,
  hasPreregistrationConflict,
  getUploadErrorMessage,
  getDuplicateUserOrEmailMessage,
} from "../utils/request-utils";
import {
  getFirstValidationMessage,
  buildSubmitBlockers,
  buildValidationErrors,
} from "../utils/validation";

const useAction = () => {
  const { addToast } = useToasts();
  const [form, setForm] = useState(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingIdentity, setIsValidatingIdentity] = useState(false);
  const [suggestedUsername, setSuggestedUsername] = useState("");
  const [result, setResult] = useState({ type: "", message: "" });
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [identityValidation, setIdentityValidation] = useState(
    INITIAL_IDENTITY_VALIDATION,
  );
  const [representativeValidation, setRepresentativeValidation] = useState(
    INITIAL_REPRESENTATIVE_VALIDATION,
  );
  const [isRepresentativeUserModalOpen, setIsRepresentativeUserModalOpen] =
    useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const shouldRecoverLastSubmissionRef = useRef(false);
  const {
    documents, supportingDocuments, specialityEvidenceDocuments,
    galleryDocuments, activeDocument, activeDocumentIndex, isDocumentsModalOpen,
    onFilesChange: addFiles, openSupportingDocumentsModal,
    openSpecialityEvidenceModal, closeDocumentsModal,
    goToPreviousDocument, goToNextDocument, removeDocument,
    onRequirementFileChange,
    onSpecialityEvidenceFileChange,
    removeGalleryDocumentAt,
    setActiveDocumentIndex, resetDocuments, removeSpecialityEvidence,
    removeSpecialityEvidenceById,
  } = useDocuments({
    maxSupportingDocuments: getRequiredSupportingDocumentsCount(form),
    isSubmitting,
    onResult: (nextResult) => {
      setResult(nextResult);
      if (nextResult.message) addToast(nextResult.message, {
        appearance: nextResult.type === "danger" ? "error" : "warning",
        autoDismiss: true,
      });
    },
  });

  const apiBaseUrl = useMemo(
    () => normalizeText(process.env.REACT_APP_APOLLO_CLIENT_URI).replace(/\/$/, ""),
    [],
  );
  const {
    provinces, specialities, isLoadingSpecialities, physicalMunicipalities,
    physicalSectors, businessMunicipalities, businessSectors, catalogError,
  } = useCatalogs(apiBaseUrl, form);
  const hasSelectedPersonType = Boolean(normalizeText(form.personType));
  const hasSelectedMedicalType = form.isMedical === true || form.isMedical === false;
  const legalChecksCompleted = Boolean(form.termsAccepted && form.privacyAccepted);
  const generatedUsername = useMemo(() => {
    if (form.personType === "MORAL") {
      return buildUsernamePreview(
        form.repFirstName,
        form.repLastName,
        form.repDocumentNumber,
      );
    }

    return buildUsernamePreview(
      form.physicalFirstName,
      form.physicalLastName,
      form.documentNumber,
    );
  }, [
    form.documentNumber,
    form.personType,
    form.physicalFirstName,
    form.physicalLastName,
    form.repDocumentNumber,
    form.repFirstName,
    form.repLastName,
  ]);
  const legalUsername = normalizeText(
    representativeValidation.existingUser?.username ||
    generatedUsername ||
    suggestedUsername,
  );
  const legalDocument =
    form.personType === "FISICA"
      ? form.documentNumber
      : form.repDocumentNumber || form.documentNumber;

  const requiredDocumentRequirements = useMemo(
    () => getRequiredSupportingDocumentRequirements(form),
    [form],
  );
  const requiredDocumentsCount = requiredDocumentRequirements.length;
  const documentsTotalSize = useMemo(
    () => getDocumentsTotalSize(documents),
    [documents],
  );

  const documentRequirementsCompleted = useMemo(
    () => areDocumentRequirementsCompleted(form, documents),
    [documents, form],
  );

  const canShowLegalRequirements =
    requiredDocumentsCount > 0 &&
    supportingDocuments.length === requiredDocumentsCount &&
    !form.specialityDraftPending &&
    (!form.isMedical || ((form.specialities || []).length > 0 &&
      form.specialities.every((speciality) =>
      speciality.id && specialityEvidenceDocuments.some(
        (document) => Number(document.specialityId) === Number(speciality.id),
      )))) &&
    documentRequirementsCompleted;

  const validationErrors = useMemo(
    () =>
      buildValidationErrors({
        form,
        documents,
        documentRequirementsCompleted,
        legalChecksCompleted,
      }),
    [form, documents, documentRequirementsCompleted, legalChecksCompleted],
  );

  const inlineErrors = showValidationErrors ? validationErrors : {};

  const submitBlockers = useMemo(
    () =>
      buildSubmitBlockers({
        form,
        validationErrors,
        identityValidation,
        representativeValidation,
      }),
    [
      form,
      identityValidation,
      representativeValidation,
      validationErrors,
    ],
  );

  const isCreateDisabled =
    isSubmitting ||
    isValidatingIdentity ||
    identityValidation.status !== "validated" ||
    (form.personType === "MORAL" &&
      representativeValidation.status !== "validated") ||
    Object.keys(validationErrors).length > 0;

  const isValidateDisabled =
    isSubmitting ||
    isValidatingIdentity ||
    !hasSelectedMedicalType ||
    !hasSelectedPersonType ||
    !normalizeText(form.documentNumber);

  const resetIdentityValidation = useCallback(() => {
    setIdentityValidation(INITIAL_IDENTITY_VALIDATION);
    setRepresentativeValidation(INITIAL_REPRESENTATIVE_VALIDATION);
    shouldRecoverLastSubmissionRef.current = false;
  }, []);

  const resetAfterIdentityChange = useCallback(() => {
    resetDocuments();
    setSuggestedUsername("");
    setShowValidationErrors(false);
    setResult({ type: "", message: "" });
    setForm((prev) => ({
      ...prev,
      ...RESET_AFTER_IDENTITY_CHANGE,
    }));
  }, [resetDocuments]);

  const showDuplicateToast = (message = "") => {
    const normalizedMessage = normalizeText(message);
    addToast(
      normalizedMessage ||
      "La identificación o la persona ya está registrada en el sistema. No puede volver a agregarse. Use otro número de identificación.",
      {
        appearance: "error",
        autoDismiss: true,
      },
    );
  };

  const showErrorToast = (message = "") => {
    const normalizedMessage = normalizeText(message);
    if (!normalizedMessage) {
      return;
    }

    addToast(normalizedMessage, {
      appearance: "error",
      autoDismiss: true,
    });
  };

  const hydrateRejectedPreregistration = (preregistration) => {
    if (!preregistration?.id) {
      return false;
    }

    const nextPersonType = normalizeText(preregistration.personType).toUpperCase();
    const nextDocumentType = normalizeText(preregistration.documentType).toUpperCase();
    const nextSpecialities = (preregistration.specialities || [])
      .map((speciality) => ({ key: `speciality-${speciality.id}`, id: Number(speciality.id) }))
      .filter((speciality) => speciality.id > 0);
    const nextRequirements = (Array.isArray(preregistration.documentRequirements)
      ? preregistration.documentRequirements
      : []
    ).reduce((result, requirement) => ({
      ...result,
      [requirement.key]: Boolean(requirement.checked),
    }), {});
    const formattedDocument = nextDocumentType === "CEDULA"
      ? formatCedula(preregistration.identificationCard)
      : nextDocumentType === "RNC"
        ? formatRnc(preregistration.identificationCard)
        : normalizeDocumentByType(preregistration.identificationCard, nextDocumentType);
    const formattedRepresentativeDocument = preregistration.representativeDocumentType === "CEDULA"
      ? formatCedula(preregistration.representativeDocumentNumber)
      : normalizeDocumentByType(
        preregistration.representativeDocumentNumber,
        preregistration.representativeDocumentType,
      );

    resetDocuments();
    setForm((previous) => ({
      ...previous,
      preregistrationId: preregistration.id,
      personType: nextPersonType,
      documentType: nextDocumentType,
      documentNumber: formattedDocument,
      isMedical: Boolean(preregistration.isMedical),
      physicalFirstName: normalizeText(preregistration.firstName),
      physicalMiddleName: normalizeText(preregistration.middleName),
      physicalLastName: normalizeText(preregistration.lastName),
      physicalLastName2: normalizeText(preregistration.lastName2),
      physicalCellphone: getContactPhones(preregistration).cellphone,
      physicalPhone: getContactPhones(preregistration).phone,
      physicalPhone2: getContactPhones(preregistration).phone2,
      physicalEmail: normalizeText(preregistration.email),
      physicalAddress: normalizeText(preregistration.address),
      physicalProvince: preregistration.provinceId ? String(preregistration.provinceId) : "",
      physicalMunicipality: preregistration.municipalityId ? String(preregistration.municipalityId) : "",
      physicalSector: preregistration.sectorId ? String(preregistration.sectorId) : "",
      physicalExequatur: normalizeText(preregistration.exequatur),
      specialities: nextSpecialities,
      businessName: normalizeText(preregistration.businessName),
      commercialName: normalizeText(preregistration.commercialName),
      businessEmail: normalizeText(preregistration.businessEmail),
      businessPhone: normalizeText(preregistration.businessPhone),
      businessAddress: normalizeText(preregistration.address),
      businessProvince: preregistration.provinceId ? String(preregistration.provinceId) : "",
      businessMunicipality: preregistration.municipalityId ? String(preregistration.municipalityId) : "",
      businessSector: preregistration.sectorId ? String(preregistration.sectorId) : "",
      officeName: normalizeText(preregistration.officeName),
      branchName: normalizeText(preregistration.branchName),
      repDocumentType: normalizeText(preregistration.representativeDocumentType).toUpperCase() || "CEDULA",
      repDocumentNumber: formattedRepresentativeDocument,
      repFirstName: normalizeText(preregistration.firstName),
      repMiddleName: normalizeText(preregistration.middleName),
      repLastName: normalizeText(preregistration.lastName),
      repLastName2: normalizeText(preregistration.lastName2),
      repCellphone: getContactPhones(preregistration).cellphone,
      repPhone: getContactPhones(preregistration).phone,
      repPhone2: getContactPhones(preregistration).phone2,
      repEmail: normalizeText(preregistration.email),
      documentRequirements: nextRequirements,
      termsAccepted: false,
      privacyAccepted: false,
    }));
    setIdentityValidation({
      status: "validated",
      message: "Solicitud rechazada encontrada. Se cargaron sus datos para corregirla.",
      foundData: true,
      taxpayerFound: true,
    });
    setResult({
      type: "danger",
      message: `Su solicitud fue rechazada. Motivo: ${normalizeText(preregistration.rejectionReason) || "No se registró un motivo."} Revise los datos, vuelva a adjuntar los documentos y reenvíe la solicitud.`,
    });
    return true;
  };

  const onChange = (key, value) => {
    shouldRecoverLastSubmissionRef.current = false;

    if (RESET_IDENTITY_VALIDATION_FIELDS.has(key)) {
      resetIdentityValidation();
      resetAfterIdentityChange();
    }

    const locationPrefix = key.startsWith("physical") ? "physical" : "business";
    if (key === `${locationPrefix}Province` || key === `${locationPrefix}Municipality`) {
      setForm((previous) => ({
        ...previous,
        [key]: value,
        ...(key.endsWith("Province") ? { [`${locationPrefix}Municipality`]: "" } : {}),
        [`${locationPrefix}Sector`]: "",
      }));
      return;
    }

    if (key === "isMedical") {
      removeSpecialityEvidence();
    }

    if (key === "isMedical") {
      const isMedical = value === true;
      setForm((prev) => ({
        ...prev,
        isMedical,
        documentRequirements: {},
        ...(isMedical
          ? { specialities: [], specialityDraftPending: false }
          : {
            physicalExequatur: "",
            specialities: [],
            specialityDraftPending: false,
          }),
      }));
      return;
    }

    if (key === "personType") {
      const nextType = normalizeText(value).toUpperCase();
      const nextDocumentType = nextType === "MORAL" ? "RNC" : "CEDULA";
      setSuggestedUsername("");
      setForm((prev) => ({
        ...prev,
        personType: nextType,
        documentType: nextDocumentType,
        documentNumber: "",
        documentRequirements: {},
      }));
      return;
    }

    if (key === "documentType") {
      const nextDocumentType = normalizeText(value).toUpperCase();
      setSuggestedUsername("");
      setForm((prev) => ({
        ...prev,
        documentType: nextDocumentType,
        documentNumber:
          nextDocumentType === "CEDULA"
            ? formatCedula(prev.documentNumber)
            : nextDocumentType === "RNC"
              ? formatRnc(prev.documentNumber)
              : normalizeDocumentByType(prev.documentNumber, nextDocumentType),
      }));
      return;
    }

    if (key === "repDocumentType") {
      const nextDocumentType = normalizeText(value).toUpperCase();
      setRepresentativeValidation(INITIAL_REPRESENTATIVE_VALIDATION);
      setIsRepresentativeUserModalOpen(false);
      setForm((prev) => ({
        ...prev,
        repDocumentType: nextDocumentType,
        repDocumentNumber:
          nextDocumentType === "CEDULA"
            ? formatCedula(prev.repDocumentNumber)
            : normalizeDocumentByType(
              prev.repDocumentNumber,
              nextDocumentType,
            ),
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onAddSpeciality = (specialityId) => {
    setForm((prev) => ({
      ...prev,
      specialities: [
        ...(prev.specialities || []),
        { key: `speciality-${specialityId}`, id: specialityId },
      ],
    }));
  };

  const onRemoveSpeciality = (index) => {
    const removed = (form.specialities || [])[index];
    if (removed?.id) removeSpecialityEvidenceById(removed.id);
    setForm((prev) => ({
      ...prev,
      specialities: (prev.specialities || []).filter((_, position) => position !== index),
    }));
  };

  const onSetSpecialities = (selectedSpecialities = []) => {
    const selectedIds = new Set(
      selectedSpecialities.map((speciality) => Number(speciality.id)),
    );
    (form.specialities || []).forEach((speciality) => {
      if (!selectedIds.has(Number(speciality.id))) {
        removeSpecialityEvidenceById(speciality.id);
      }
    });
    setForm((prev) => ({
      ...prev,
      specialityDraftPending: false,
      specialities: selectedSpecialities.map((speciality) => ({
        key: `speciality-${speciality.id}`,
        id: speciality.id,
      })),
    }));
  };

  const onDocumentRequirementChange = (key, checked) => {
    shouldRecoverLastSubmissionRef.current = false;
    setForm((prev) => ({
      ...prev,
      documentRequirements: {
        ...prev.documentRequirements,
        [key]: checked,
      },
    }));
  };

  const onDocumentNumberChange = (value) => {
    const currentType = normalizeText(form.documentType).toUpperCase();

    if (currentType === "IDENTIFICACION") {
      onChange("documentNumber", normalizeText(value).toUpperCase());
      return;
    }

    if (currentType === "CEDULA") {
      onChange("documentNumber", formatCedula(value));
      return;
    }

    if (currentType === "RNC") {
      onChange("documentNumber", formatRnc(value));
      return;
    }

    onChange("documentNumber", normalizePassport(value));
  };

  const onRepresentativeDocumentNumberChange = (value) => {
    setRepresentativeValidation(INITIAL_REPRESENTATIVE_VALIDATION);
    setIsRepresentativeUserModalOpen(false);
    setSuggestedUsername("");
    const currentType = normalizeText(form.repDocumentType).toUpperCase();
    const nextValue =
      currentType === "CEDULA" ? formatCedula(value) : normalizePassport(value);

    setForm((prev) => ({
      ...prev,
      repDocumentNumber: nextValue,
      repFirstName: "",
      repMiddleName: "",
      repLastName: "",
      repLastName2: "",
      repCellphone: "",
      repPhone: "",
      repPhone2: "",
      repEmail: "",
      physicalExequatur: "",
    }));
  };

  const handleValidateRepresentativeIdentity = async () => {
    if (!apiBaseUrl) {
      setResult({
        type: "danger",
        message: "No se encontro la configuracion del API.",
      });
      return;
    }

    const representativeDocumentType = normalizeText(form.repDocumentType).toUpperCase();
    const representativeIdentificationCard = normalizeDocumentByType(
      form.repDocumentNumber,
      representativeDocumentType,
    );

    const documentValidationMessage = getDocumentValidationMessage(
      representativeDocumentType,
      form.repDocumentNumber,
    );

    if (documentValidationMessage) {
      setRepresentativeValidation({
        status: "error",
        message: documentValidationMessage,
      });
      setResult({
        type: "danger",
        message: documentValidationMessage,
      });
      return;
    }

    setIsValidatingIdentity(true);
    setRepresentativeValidation(INITIAL_REPRESENTATIVE_VALIDATION);
    setIsRepresentativeUserModalOpen(false);
    setSuggestedUsername("");
    setForm((prev) => ({
      ...prev,
      repFirstName: "",
      repMiddleName: "",
      repLastName: "",
      repLastName2: "",
      repCellphone: "",
      repPhone: "",
      repPhone2: "",
      repEmail: "",
      physicalExequatur: "",
    }));

    try {
      const params = new URLSearchParams();
      params.set("validationScope", "representative");
      params.set("personType", "MORAL");
      params.set("documentType", representativeDocumentType);
      params.set("identificationCard", representativeIdentificationCard);

      if (hasSelectedMedicalType) {
        params.set("isMedical", String(form.isMedical));
      }

      const response = await fetch(
        `${apiBaseUrl}/public/medical-preregistrations/validate?${params.toString()}`,
      );
      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await response.json()
        : {};

      const responseExistingUser = data?.lookups?.user || null;
      const medicalLookup = data?.lookups?.medicalStaff || null;
      const existingUserDocument = normalizeDocumentByType(
        responseExistingUser?.identification ||
        responseExistingUser?.identificationCard ||
        medicalLookup?.identificationCard,
        representativeDocumentType,
      );
      const existingUser =
        responseExistingUser &&
          (!existingUserDocument ||
            existingUserDocument === representativeIdentificationCard)
          ? responseExistingUser
          : null;

      if (response.ok && existingUser) {
        const lockedFields = {};
        const lockIfValue = (fieldName, value) => {
          if (normalizeText(value)) {
            lockedFields[fieldName] = true;
          }
        };

        lockIfValue("repFirstName", medicalLookup?.firstName || existingUser.firstName);
        lockIfValue("repMiddleName", medicalLookup?.middleName);
        lockIfValue("repLastName", medicalLookup?.lastName || existingUser.lastName);
        lockIfValue("repLastName2", medicalLookup?.lastName2);
        lockIfValue("repEmail", medicalLookup?.email || existingUser.email);
        const lookupPhones = getContactPhones(medicalLookup);
        lockIfValue("repCellphone", lookupPhones.cellphone);
        lockIfValue("repPhone", lookupPhones.phone);
        lockIfValue("repPhone2", lookupPhones.phone2);
        lockIfValue("physicalExequatur", medicalLookup?.exequatur);

        setForm((prev) => ({
          ...prev,
          repFirstName: normalizeText(medicalLookup?.firstName || existingUser.firstName),
          repMiddleName: normalizeText(medicalLookup?.middleName),
          repLastName: normalizeText(medicalLookup?.lastName || existingUser.lastName),
          repLastName2: normalizeText(medicalLookup?.lastName2),
          repEmail: normalizeText(medicalLookup?.email || existingUser.email),
          repCellphone: lookupPhones.cellphone,
          repPhone: lookupPhones.phone,
          repPhone2: lookupPhones.phone2,
          physicalExequatur: normalizeText(medicalLookup?.exequatur),
        }));

        if (normalizeText(existingUser.username)) {
          setSuggestedUsername(existingUser.username);
        }

        setRepresentativeValidation({
          status: "validated",
          message:
            medicalLookup && form.isMedical
              ? "Documento del representante validado correctamente. Se mantendrá el usuario y los datos médicos existentes."
              : form.isMedical
                ? "Documento del representante validado correctamente. Se mantendrá el usuario existente y debe completar los datos médicos faltantes."
                : "Documento del representante validado correctamente. Se mantendrá el usuario existente.",
          existingUser,
          existingMedicalStaff: medicalLookup,
          lockedFields,
        });
        setIsRepresentativeUserModalOpen(true);
        setResult({
          type: "success",
          message:
            medicalLookup && form.isMedical
              ? "Documento del representante validado correctamente. Se mantendrá el usuario y los datos médicos existentes."
              : "Documento del representante validado correctamente. Se mantendrá el usuario existente.",
        });
        return;
      }

      if (!response.ok || !data?.available) {
        const fallbackMessage =
          data?.message ||
          response.statusText ||
          "La cédula o pasaporte del representante ya existe o no está disponible.";
        const message = getDuplicateUserOrEmailMessage(data, fallbackMessage);
        showDuplicateToast(message);
        setRepresentativeValidation({
          status: "conflict",
          message,
        });
        setResult({
          type: "danger",
          message,
        });
        return;
      }

      setRepresentativeValidation({
        status: "validated",
        message: "Documento del representante validado correctamente.",
        existingUser: null,
      });
      setResult({
        type: "success",
        message: "Documento del representante validado correctamente.",
      });
    } catch (error) {
      const message =
        "No fue posible validar el documento del representante. Verifique conexión con el API.";
      setRepresentativeValidation({
        status: "error",
        message,
      });
      setResult({
        type: "danger",
        message,
      });
    } finally {
      setIsValidatingIdentity(false);
    }
  };

  const onFilesChange = (event, evidence) => {
    shouldRecoverLastSubmissionRef.current = false;
    addFiles(event, evidence);
  };

  const handleValidateIdentity = async () => {
    const documentType = normalizeText(form.documentType).toUpperCase();
    const identificationCard = normalizeDocumentByType(form.documentNumber, documentType);
    const firstName = normalizeText(
      form.personType === "FISICA" ? form.physicalFirstName : "",
    );
    const lastName = normalizeText(
      form.personType === "FISICA" ? form.physicalLastName : "",
    );
    const email = normalizeText(
      form.personType === "FISICA" ? form.physicalEmail : form.businessEmail,
    ).toLowerCase();

    const documentValidationMessage = getDocumentValidationMessage(
      documentType,
      form.documentNumber,
    );

    if (documentValidationMessage) {
      setShowValidationErrors(true);
      setResult({
        type: "danger",
        message: documentValidationMessage,
      });
      return;
    }

    if (!apiBaseUrl) {
      const message = "No se encontro la configuracion del API.";
      setResult({
        type: "danger",
        message,
      });
      return;
    }

    setIsValidatingIdentity(true);
    setSuggestedUsername("");
    setIdentityValidation(INITIAL_IDENTITY_VALIDATION);

    try {
      const params = new URLSearchParams();
      if (identificationCard) {
        params.set("identificationCard", identificationCard);
      }
      if (email) {
        params.set("email", email);
      }
      if (documentType) {
        params.set("documentType", documentType);
      }
      if (form.personType) {
        params.set("personType", form.personType);
      }
      if (form.personType === "MORAL") {
        params.set("validationScope", "business");
      }
      if (hasSelectedMedicalType) {
        params.set("isMedical", String(form.isMedical));
      }
      if (firstName) {
        params.set("firstName", firstName);
      }
      if (lastName) {
        params.set("lastName", lastName);
      }

      const response = await fetch(
        `${apiBaseUrl}/public/medical-preregistrations/validate?${params.toString()}`,
      );

      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await response.json()
        : {};

      if (!response.ok) {
        const message =
          data?.message ||
          response.statusText ||
          "No fue posible validar la informacion.";
        showErrorToast(message);
        setIdentityValidation({
          status: "error",
          message,
          foundData: false,
        });
        setResult({
          type: "danger",
          message,
        });
        return;
      }

      const medicalLookup = data?.lookups?.medicalStaff;
      const taxpayerLookup = data?.lookups?.taxpayer;

      if (data?.conflicts?.preregistrationRejected && data?.lookups?.preregistration) {
        hydrateRejectedPreregistration(data.lookups.preregistration);
        return;
      }

      if (normalizeText(data?.suggestedUsername)) {
        setSuggestedUsername(data.suggestedUsername);
      }

      if (medicalLookup && form.personType === "FISICA") {
        setForm((prev) => ({
          ...prev,
          physicalFirstName: normalizeText(medicalLookup.firstName || prev.physicalFirstName),
          physicalMiddleName: normalizeText(medicalLookup.middleName || prev.physicalMiddleName),
          physicalLastName: normalizeText(medicalLookup.lastName || prev.physicalLastName),
          physicalLastName2: normalizeText(medicalLookup.lastName2 || prev.physicalLastName2),
          physicalEmail: normalizeText(medicalLookup.email || prev.physicalEmail),
          physicalCellphone: getContactPhones(medicalLookup).cellphone || prev.physicalCellphone,
          physicalPhone: getContactPhones(medicalLookup).phone || prev.physicalPhone,
          physicalPhone2: getContactPhones(medicalLookup).phone2 || prev.physicalPhone2,
          physicalExequatur: normalizeText(medicalLookup.exequatur || prev.physicalExequatur),
          physicalAddress: normalizeText(medicalLookup.address || prev.physicalAddress),
          physicalProvince: medicalLookup.provinceId
            ? String(medicalLookup.provinceId)
            : prev.physicalProvince,
          physicalMunicipality: medicalLookup.municipalityId
            ? String(medicalLookup.municipalityId)
            : prev.physicalMunicipality,
          physicalSector: medicalLookup.sectorId
            ? String(medicalLookup.sectorId)
            : prev.physicalSector,
        }));
      }

      if (taxpayerLookup) {
        setForm((prev) => ({
          ...prev,
          businessName: normalizeText(taxpayerLookup.businessName || prev.businessName),
          commercialName: normalizeText(taxpayerLookup.commercialName || prev.commercialName),
        }));
      }

      if (data?.conflicts?.medicalStaff) {
        const message =
          "Ya existe un medico activo con la cédula/RNC o correo indicado. No puede crear un preregistro nuevo.";
        showDuplicateToast(message);
        setIdentityValidation({
          status: "conflict",
          message,
          foundData: false,
          taxpayerFound: false,
        });
        setResult({
          type: "danger",
          message,
        });
        return;
      }

      if (!data?.available) {
        const conflictMessages = [
          data?.conflicts?.taxpayerNotFound
            ? TAXPAYER_NOT_FOUND_MESSAGE
            : "",
          data?.conflicts?.preregistrationPending
            ? "Ya existe una solicitud previa con este número de identificación y está en espera de aprobación."
            : data?.conflicts?.preregistration
              ? "Ya existe una solicitud previa con la cédula/RNC o correo."
              : "",
          data?.conflicts?.user
            ? "Ya existe un usuario activo con la cédula/RNC o correo."
            : "",
          data?.conflicts?.provider
            ? DUPLICATED_PROVIDER_RNC_MESSAGE
            : "",
          data?.conflicts?.auth0Email
            ? "El correo ya existe en la plataforma de autenticacion."
            : "",
          data?.conflicts?.auth0Username
            ? "El usuario sugerido choca con Auth0."
            : "",
          data?.conflicts?.usernameInDatabase
            ? "El usuario sugerido choca con la base de datos."
            : "",
        ]
          .filter(Boolean)
          .join(" ");

        const message =
          conflictMessages ||
          data?.message ||
          "Se detectaron conflictos de duplicidad. Revise la informacion ingresada.";

        const duplicateConflict =
          Boolean(data?.conflicts?.preregistration) ||
          Boolean(data?.conflicts?.medicalStaff) ||
          Boolean(data?.conflicts?.user) ||
          Boolean(data?.conflicts?.provider) ||
          Boolean(data?.conflicts?.taxpayerNotFound) ||
          Boolean(data?.conflicts?.auth0Email);

        if (duplicateConflict) {
          showErrorToast(message);
        }

        setIdentityValidation({
          status: "conflict",
          message,
          foundData: false,
        });

        setResult({
          type: "danger",
          message,
        });
        return;
      }

      if (data?.available) {
        const foundData = Boolean(medicalLookup || taxpayerLookup);
        const autoFillMessage = [
          medicalLookup ? "Se autocompletaron datos del medico existente." : "",
          taxpayerLookup ? "Se autocompleto la razon social desde el registro fiscal." : "",
          normalizeText(data?.suggestedUsername)
            ? `Usuario sugerido: ${data.suggestedUsername}.`
            : "",
        ]
          .filter(Boolean)
          .join(" ");

        const message = foundData
          ? "Validación correcta: se encontraron datos y se autocompletaron campos."
          : form.personType === "MORAL"
            ? "Validación correcta: RNC validado. Llene los datos para continuar."
            : form.isMedical
              ? "No se encontró médico con este documento. Llene el formulario para registro."
              : "No se encontró usuario con este documento. Llene el formulario para registro.";

        setIdentityValidation({
          status: "validated",
          message,
          foundData,
          taxpayerFound: Boolean(taxpayerLookup),
        });

        setResult({
          type: "success",
          message: message + (autoFillMessage ? ` ${autoFillMessage}` : ""),
        });
        return;
      }

      const fallbackMessage =
        data?.message ||
        "Ya existe una solicitud registrada con la cédula/RNC o correo indicado.";
      setIdentityValidation({
        status: "error",
        message: fallbackMessage,
        foundData: false,
        taxpayerFound: false,
      });
      setResult({
        type: "danger",
        message: fallbackMessage,
      });
    } catch (error) {
      const message =
        "No fue posible conectar con el servidor de validación. Verifique que el API esté disponible.";
      setIdentityValidation({
        status: "error",
        message,
        foundData: false,
        taxpayerFound: false,
      });
      setResult({
        type: "danger",
        message,
      });
    } finally {
      setIsValidatingIdentity(false);
    }
  };

  const validateForm = () => getFirstValidationMessage(validationErrors);

  const buildSubmitValidationTargets = () => {
    const documentType = normalizeText(form.documentType).toUpperCase();
    const mainIdentificationCard = normalizeDocumentByType(
      form.documentNumber,
      documentType,
    );
    const representativeDocumentType = normalizeText(form.repDocumentType).toUpperCase();
    const representativeIdentificationCard = normalizeDocumentByType(
      form.repDocumentNumber,
      representativeDocumentType,
    );

    const targets =
      form.personType === "MORAL"
        ? [
          {
            validationScope: "business",
            documentType,
            identificationCard: mainIdentificationCard,
            email: normalizeText(form.businessEmail).toLowerCase(),
            firstName: "",
            lastName: "",
          },
          representativeValidation.existingUser?.id
            ? null
            : {
              validationScope: "representative",
              documentType: representativeDocumentType,
              identificationCard: representativeIdentificationCard,
              email: normalizeText(form.repEmail).toLowerCase(),
              firstName: normalizeText(form.repFirstName),
              lastName: normalizeText(form.repLastName),
            },
        ].filter(Boolean)
        : [
          {
            validationScope: "person",
            documentType,
            identificationCard: mainIdentificationCard,
            email: normalizeText(form.physicalEmail).toLowerCase(),
            firstName: normalizeText(form.physicalFirstName),
            lastName: normalizeText(form.physicalLastName),
          },
        ];

    return targets.filter((target) => target.identificationCard || target.email);
  };

  const fetchSubmitValidation = async (target) => {
    const params = new URLSearchParams();
    if (target.identificationCard) {
      params.set("identificationCard", target.identificationCard);
    }
    if (target.email) {
      params.set("email", target.email);
    }
    if (target.documentType) {
      params.set("documentType", target.documentType);
    }
    if (form.personType) {
      params.set("personType", form.personType);
    }
    if (target.validationScope) {
      params.set("validationScope", target.validationScope);
    }
    if (hasSelectedMedicalType) {
      params.set("isMedical", String(form.isMedical));
    }
    if (target.firstName) {
      params.set("firstName", target.firstName);
    }
    if (target.lastName) {
      params.set("lastName", target.lastName);
    }

    const response = await fetch(
      `${apiBaseUrl}/public/medical-preregistrations/validate?${params.toString()}`,
    );
    const contentType = response.headers.get("content-type") || "";
    const data = contentType.includes("application/json")
      ? await response.json()
      : {};

    return { response, data };
  };

  const completeRecoveredSubmission = () => {
    shouldRecoverLastSubmissionRef.current = false;
    setResult({ type: "", message: "" });
    setSuccessMessage(RECOVERED_SUBMISSION_MESSAGE);
    setIsSuccessModalOpen(true);
  };

  const recoverSubmittedPreregistration = async () => {
    const targets = buildSubmitValidationTargets();
    if (!targets.length) {
      return false;
    }

    for (const delayMs of RECOVERY_ATTEMPT_DELAYS_MS) {
      if (delayMs) {
        await wait(delayMs);
      }

      try {
        for (const target of targets) {
          const { data } = await fetchSubmitValidation(target);
          if (hasPreregistrationConflict(data)) {
            completeRecoveredSubmission();
            return true;
          }
        }
      } catch (error) {
        // Keep polling; transient local network errors are the scenario being recovered.
      }
    }

    return false;
  };

  const validateCurrentIdentityBeforeSubmit = async ({
    allowPendingPreregistrationRecovery = false,
  } = {}) => {
    if (!apiBaseUrl) {
      setResult({
        type: "danger",
        message: "No se encontro la configuracion del API.",
      });
      return false;
    }

    const validTargets = buildSubmitValidationTargets();

    if (!validTargets.length) {
      setResult({
        type: "danger",
        message: "Debe indicar un correo electrónico para validar.",
      });
      return false;
    }

    setIsValidatingIdentity(true);

    try {
      for (const target of validTargets) {
        const { response, data } = await fetchSubmitValidation(target);

        if (!response.ok || !data?.available) {
          if (allowPendingPreregistrationRecovery && hasPreregistrationConflict(data)) {
            completeRecoveredSubmission();
            return "recovered";
          }

          const fallbackMessage =
            data?.message ||
            response.statusText ||
            "Ya existe un usuario o solicitud con esta identificación o correo.";
          const message = getDuplicateUserOrEmailMessage(data, fallbackMessage);

          if (
            response.status === 409 ||
            Boolean(data?.conflicts?.preregistration) ||
            Boolean(data?.conflicts?.medicalStaff) ||
            Boolean(data?.conflicts?.user) ||
            Boolean(data?.conflicts?.provider) ||
            Boolean(data?.conflicts?.taxpayerNotFound) ||
            Boolean(data?.conflicts?.auth0Email) ||
            Boolean(data?.conflicts?.auth0Username) ||
            Boolean(data?.conflicts?.usernameInDatabase) ||
            /existe|registrad|solicitud/i.test(message)
          ) {
            showErrorToast(message);
          }

          setResult({
            type: "danger",
            message,
          });
          return false;
        }

        if (normalizeText(data?.suggestedUsername)) {
          setSuggestedUsername(data.suggestedUsername);
        }
      }

      setIdentityValidation((prev) => ({
        status: "validated",
        message: "Validación correcta: la identificación y el correo están disponibles.",
        foundData: Boolean(prev.foundData),
        taxpayerFound: Boolean(prev.taxpayerFound),
      }));
      return true;
    } catch (error) {
      const message =
        "No fue posible conectar con el servidor de validación. Verifique que el API esté disponible.";
      setResult({
        type: "danger",
        message,
      });
      return false;
    } finally {
      setIsValidatingIdentity(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowValidationErrors(true);

    if (identityValidation.status !== "validated") {
      setResult({
        type: "danger",
        message: "Debe validar el documento en el Paso 1 antes de continuar.",
      });
      return;
    }

    if (
      form.personType === "MORAL" &&
      representativeValidation.status !== "validated"
    ) {
      setResult({
        type: "danger",
        message:
          "Debe validar la cédula o pasaporte del representante legal antes de crear la solicitud.",
      });
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setResult({ type: "danger", message: validationError });
      return;
    }

    if (!apiBaseUrl) {
      setResult({
        type: "danger",
        message: "No se encontro la configuracion del API.",
      });
      return;
    }

    setIsSubmitting(true);
    setResult({ type: "", message: "" });

    try {
      const identityValidationResult = await validateCurrentIdentityBeforeSubmit({
        allowPendingPreregistrationRecovery: shouldRecoverLastSubmissionRef.current,
      });
      if (identityValidationResult === "recovered") {
        return;
      }
      if (!identityValidationResult) {
        return;
      }

      const payload = buildPreregistrationPayload(form, documents, representativeValidation);

      const response = await fetch(`${apiBaseUrl}/public/medical-preregistrations`, {
        method: "POST",
        body: buildSubmitFormData(payload, documents),
      });

      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await response.json()
        : {};

      if (!response.ok) {
        const fallbackMessage = getUploadErrorMessage({ response, data });
        const message = getDuplicateUserOrEmailMessage(data, fallbackMessage);
        showErrorToast(message);
        setResult({
          type: "danger",
          message,
        });
        return;
      }

      setResult({ type: "", message: "" });
      setSuccessMessage(data?.message || "Solicitud enviada correctamente.");
      setIsSuccessModalOpen(true);
      shouldRecoverLastSubmissionRef.current = false;
    } catch (error) {
      if (isUploadNetworkError(error)) {
        shouldRecoverLastSubmissionRef.current = true;
        const wasRecovered = await recoverSubmittedPreregistration();
        if (wasRecovered) {
          return;
        }
      }

      const message = getUploadErrorMessage({ error });
      showErrorToast(message);
      setResult({
        type: "danger",
        message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = ({ silent = false } = {}) => {
    resetDocuments();

    setForm(INITIAL_STATE);
    setIsSubmitting(false);
    setIsValidatingIdentity(false);
    setSuggestedUsername("");
    setShowValidationErrors(false);
    setIdentityValidation(INITIAL_IDENTITY_VALIDATION);
    setRepresentativeValidation(INITIAL_REPRESENTATIVE_VALIDATION);
    setIsRepresentativeUserModalOpen(false);
    setIsSuccessModalOpen(false);
    setSuccessMessage("");
    shouldRecoverLastSubmissionRef.current = false;
    setResult(
      silent
        ? { type: "", message: "" }
        : {
          type: "info",
          message: "Formulario reiniciado. Puede validar una nueva persona.",
        },
    );
  };

  const startAnotherRequest = () => {
    resetForm({ silent: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    document.body.classList.toggle("medical-prereg-success-open", isSuccessModalOpen);

    return () => {
      document.body.classList.remove("medical-prereg-success-open");
    };
  }, [isSuccessModalOpen]);

  const closeRepresentativeUserModal = () => {
    setIsRepresentativeUserModalOpen(false);
  };

  return {
    state: {
      form,
      documents,
      supportingDocuments,
      specialityEvidenceDocuments,
      galleryDocuments,
      activeDocument,
      activeDocumentIndex,
      isDocumentsModalOpen,
      isSubmitting,
      isValidatingIdentity,
      result,
      catalogError,
      provinces,
      specialities,
      isLoadingSpecialities,
      physicalMunicipalities,
      physicalSectors,
      businessMunicipalities,
      businessSectors,
      hasSelectedPersonType,
      hasSelectedMedicalType,
      legalChecksCompleted,
      legalUsername,
      legalDocument,
      suggestedUsername,
      requiredDocumentRequirements,
      requiredDocumentsCount,
      documentsTotalSize,
      inlineErrors,
      submitBlockers,
      isCreateDisabled,
      isValidateDisabled,
      documentRequirementsCompleted,
      canShowLegalRequirements,
      identityValidation,
      representativeValidation,
      isRepresentativeUserModalOpen,
      isSuccessModalOpen,
      successMessage,
    },
    actions: {
      onChange,
      onDocumentNumberChange,
      onRepresentativeDocumentNumberChange,
      onDocumentRequirementChange,
      onRequirementFileChange,
      onSpecialityEvidenceFileChange,
      onSetSpecialities,
      onAddSpeciality,
      onRemoveSpeciality,
      removeSpecialityEvidenceById,
      onFilesChange,
      openSupportingDocumentsModal,
      openSpecialityEvidenceModal,
      closeDocumentsModal,
      goToPreviousDocument,
      goToNextDocument,
      removeDocument,
      removeGalleryDocumentAt,
      setActiveDocumentIndex,
      handleValidateIdentity,
      handleValidateRepresentativeIdentity,
      handleSubmit,
      resetForm,
      startAnotherRequest,
      closeRepresentativeUserModal,
    },
  };
};

export default useAction;
